import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as BlockedDateType from 'automation-ui-canvas/flow/constants/BlockedDateType';
import * as SimpleDate from 'foundations-components/transitional/utils/SimpleDate';
import { List as ListComponent } from 'foundations-components/transitional/components/List';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { DeleteIcon, InfoIcon } from 'foundations-assets';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { Flex } from 'foundations-components/components/Flex';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TODAY = SimpleDate.now();
const TODAY_NEXT_YEAR = SimpleDate.fromMoment(SimpleDate.toMoment(SimpleDate.now()).add(1, 'year'));
const getIsRecurringChecked = type => type === BlockedDateType.RECURRING;
const getIsDateValid = (date, type) => {
  if (!date) {
    return false;
  }

  // date <= TODAY_NEXT_YEAR
  const isBeforeMax = !getIsRecurringChecked(type) || SimpleDate.compare(date, TODAY_NEXT_YEAR) !== 1;

  // date >= TODAY
  const isAfterMin = SimpleDate.compare(date, TODAY) !== -1;
  return isBeforeMax && isAfterMin;
};
const getTooltipTitle = (error, isRecurringChecked) => {
  if (error) {
    return isRecurringChecked ? I18n.text('platformSettings.holidayManagement.maxValidation') : I18n.text('platformSettings.holidayManagement.dateValidation');
  }
  return null;
};
const getDeleteTooltip = (isDateToday, readOnly) => {
  let deleteTooltip = null;
  if (isDateToday) {
    deleteTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "platformSettings.holidayManagement.todayTooltip"
    });
  }
  // overrides isDateToday
  if (readOnly) {
    deleteTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation.common.readOnlyTooltip"
    });
  }
  return deleteTooltip;
};
const RecurringDateList = ({
  blockedDatesWithIndex,
  onRemoveDate,
  onSetDate,
  readOnly,
  use
}) => {
  const handleTypeChange = (index, blockedDate, checked) => {
    const type = checked ? BlockedDateType.RECURRING : BlockedDateType.STATIC;
    const date = blockedDate.getDate();
    const newDate = getIsDateValid(date, type) ? date : null;
    const newBlockedDate = blockedDate.setType(type).setDate(newDate);
    onSetDate(index, newBlockedDate);
  };
  const handleDateChange = (index, blockedDate, value) => {
    const newDate = getIsDateValid(value, blockedDate.getType()) ? value : null;
    const newBlockedDate = blockedDate.setDate(newDate);
    onSetDate(index, newBlockedDate);
  };
  const disabledValues = blockedDatesWithIndex.map(({
    blockedDate
  }) => blockedDate.getDate()).toArray();
  return /*#__PURE__*/_jsx(ListComponent, {
    children: blockedDatesWithIndex.map(({
      blockedDate,
      index
    }) => {
      const isDateToday = blockedDate.getDate() && SimpleDate.compare(blockedDate.getDate(), TODAY) === 0;
      const isRecurringChecked = getIsRecurringChecked(blockedDate.getType());
      const error = !getIsDateValid(blockedDate.getDate(), blockedDate.getType());
      const title = getTooltipTitle(error, isRecurringChecked);
      const deleteTooltip = getDeleteTooltip(isDateToday, readOnly);
      const deleteBtn = /*#__PURE__*/_jsx(IconButton, {
        use: use === 'compact' ? 'link-on-bright' : 'link',
        onClick: () => onRemoveDate(index),
        disabled: readOnly,
        tooltip: deleteTooltip,
        tooltipProps: {
          placement: 'right'
        },
        children: /*#__PURE__*/_jsx(DeleteIcon, {})
      });
      const dateInput = /*#__PURE__*/_jsx(DateInput, {
        use: use === 'compact' ? 'on-dark' : undefined,
        "data-test-id": `platform-settings-timing-date-picker-start-${index}`,
        min: TODAY
        // only accepts string input
        ,
        minValidationMessage: I18n.text('platformSettings.holidayManagement.minValidation'),
        max: isRecurringChecked ? TODAY_NEXT_YEAR : undefined
        // only accepts string input
        ,
        maxValidationMessage: isRecurringChecked ? I18n.text('platformSettings.holidayManagement.maxValidation') : undefined,
        onChange: ({
          target: {
            value
          }
        }) => handleDateChange(index, blockedDate, value),
        value: blockedDate.getDate(),
        error: error,
        readOnly: readOnly,
        disabledValues: disabledValues,
        inputWidth: 300
      });
      const recurringInput = /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(Checkbox, {
          marginInlineEnd: "100",
          checked: isRecurringChecked,
          disabled: readOnly,
          size: "small",
          onChange: ({
            target: {
              checked
            }
          }) => handleTypeChange(index, blockedDate, checked),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformSettings.holidayManagement.recurring"
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "platformSettings.holidayManagement.recurringTooltip"
          }),
          children: /*#__PURE__*/_jsx(InfoIcon, {})
        })]
      });
      if (use === 'compact') {
        return /*#__PURE__*/_jsx(Tile, {
          compact: true,
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-surface-raised",
            children: /*#__PURE__*/_jsx(TileSection, {
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "start",
                justifyContent: "space-between",
                children: [/*#__PURE__*/_jsxs(Flex, {
                  flexDirection: "column",
                  justifyContent: "start",
                  children: [dateInput, recurringInput]
                }), /*#__PURE__*/_jsx(View, {
                  padding: "200",
                  children: deleteBtn
                })]
              })
            })
          })
        }, index);
      }
      return /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexWrap: "nowrap",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(FormControl, {
            "aria-label": I18n.text('platformSettings.holidayManagement.ariaLabel'),
            children: /*#__PURE__*/_jsx(Tooltip, {
              title: title,
              children: dateInput
            })
          }), /*#__PURE__*/_jsx(View, {
            marginInline: "300",
            children: recurringInput
          }), deleteBtn]
        })
      }, index);
    })
  });
};
export default RecurringDateList;